/**
 * Broadcast page decision logic.
 *
 * Split out because apps/admin-ui is never ESLint'd or complexity-checked in
 * CI and its jest suite matches `.ts` only -- so pure logic in a sibling file
 * is the one part of a page that can actually be tested here.
 */

import type { BroadcastItem, BroadcastStatus, PendingActionItem } from '../api/adminOps';
import type { ChipTone } from '../components/ui/StatusChip';

/** Mirrors the notifications.title/body column widths exactly. */
export const TITLE_MAX_LENGTH = 255;
export const BODY_MAX_LENGTH = 500;

/** Statuses a cancel may still interrupt. */
const CANCELLABLE: readonly BroadcastStatus[] = [
  'draft',
  'pending_approval',
  'scheduled',
  'expanding',
  'sending',
];

const TERMINAL: readonly BroadcastStatus[] = ['completed', 'cancelled', 'failed'];

/** Statuses whose copy and audience may still be edited. */
const EDITABLE: readonly BroadcastStatus[] = ['draft', 'pending_approval'];

const STATUS_TONES: Record<BroadcastStatus, { label: string; tone: ChipTone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  pending_approval: { label: 'Awaiting approval', tone: 'warn' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  expanding: { label: 'Expanding', tone: 'info' },
  sending: { label: 'Sending', tone: 'info' },
  completed: { label: 'Completed', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'crit' },
};

export function statusChip(status: BroadcastStatus): { label: string; tone: ChipTone } {
  return STATUS_TONES[status] ?? { label: status, tone: 'neutral' };
}

export function isTerminal(status: BroadcastStatus): boolean {
  return TERMINAL.includes(status);
}

export function canCancel(status: BroadcastStatus): boolean {
  return CANCELLABLE.includes(status);
}

export function canEdit(status: BroadcastStatus): boolean {
  return EDITABLE.includes(status);
}

/** Only a draft can be submitted, and only once. */
export function canRequestApproval(status: BroadcastStatus): boolean {
  return status === 'draft';
}

export interface ComposeDraft {
  readonly title: string;
  readonly body: string;
  readonly audience: string;
}

/**
 * Why the compose form cannot be submitted yet, or null when it can. Returning
 * the reason rather than a boolean lets the button explain itself instead of
 * being mysteriously disabled.
 */
export function composeBlocker(draft: ComposeDraft): string | null {
  if (draft.title.trim().length === 0) return 'Title is required';
  if (draft.body.trim().length === 0) return 'Body is required';
  if (draft.title.length > TITLE_MAX_LENGTH) {
    return `Title is ${draft.title.length - TITLE_MAX_LENGTH} characters too long`;
  }
  if (draft.body.length > BODY_MAX_LENGTH) {
    return `Body is ${draft.body.length - BODY_MAX_LENGTH} characters too long`;
  }
  if (draft.audience.length === 0) return 'Pick an audience';
  return null;
}

/**
 * Delivery is only meaningfully "progressing" once the audience is known.
 * Before expansion finishes, recipient_count is still climbing, so a
 * percentage against it would run backwards as the denominator grew.
 */
export function deliveryProgress(broadcast: BroadcastItem): {
  readonly resolved: number;
  readonly total: number;
  readonly percent: number | null;
} {
  const resolved = broadcast.sent_count + broadcast.suppressed_count + broadcast.failed_count;
  const total = broadcast.recipient_count;
  const settled = broadcast.status !== 'expanding' && total > 0;

  return {
    resolved,
    total,
    percent: settled ? Math.min(100, Math.round((resolved / total) * 100)) : null,
  };
}

/**
 * The audience snapshot excludes anyone created after expansion ran, so the
 * preview and the delivered count legitimately differ. Saying so is the
 * difference between a trusted number and a bug report.
 */
export function audienceEstimateNote(count: number): string {
  return `About ${count.toLocaleString()} recipients. Counted now — anyone who joins before the send is not included.`;
}

export interface TestSendState {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error?: { readonly message?: string } | null;
  readonly data?: {
    readonly data?: {
      readonly devices_targeted?: number;
      readonly target_email?: string;
    };
  } | null;
}

/**
 * What to tell the operator after a test send. The test goes through the same
 * queue and worker as a real send, so it lands a moment later rather than
 * instantly -- saying "sent" with no qualification is what would make an
 * operator assume their own device was skipped and approve the mass send
 * having seen nothing.
 */
export function testSendNote(state: TestSendState): string {
  if (state.isPending) return 'Queueing a test to your own devices.';
  if (state.isError) {
    return state.error?.message ?? 'The test could not be sent.';
  }
  const devices = state.data?.data?.devices_targeted;
  const target = state.data?.data?.target_email;
  if (devices === undefined || target === undefined) {
    // Admin sign-in and app accounts are separate pools, so there is no "my
    // devices" to default to -- the account has to be named.
    return 'Sends this exact notification to one app account you name.';
  }
  return devices === 1
    ? `Test queued for the device on ${target}. It arrives the same way the real one will.`
    : `Test queued for ${devices} devices on ${target}. It arrives the same way the real one will.`;
}

/** The pending-action operation a broadcast send is gated behind. */
export const BROADCAST_SEND_OPERATION = 'broadcast.send';

/**
 * The open approval request for one broadcast, or null.
 *
 * The pending action stores the broadcast id in its payload rather than in a
 * column, so the console matches on it here. A broadcast can only ever have one
 * open request: requestApproval accepts a draft, and the request is what moves
 * it out of draft.
 */
export function pendingApprovalFor(
  actions: readonly PendingActionItem[],
  broadcastId: string
): PendingActionItem | null {
  const match = actions.find(action => {
    if (action.operation !== BROADCAST_SEND_OPERATION || action.status !== 'pending') return false;
    const payload = action.payload;
    if (typeof payload !== 'object' || payload === null) return false;
    return (payload as { broadcast_id?: unknown }).broadcast_id === broadcastId;
  });
  return match ?? null;
}

export interface ApprovalGate {
  /** Whether to render the approve/reject control at all. */
  readonly canDecide: boolean;
  /** What the viewer is told, whether or not they can act. */
  readonly note: string;
}

/**
 * How much the console actually knows about the approval request.
 *
 * `error` is deliberately not folded into "there is no request": a failed
 * lookup rendered as an absent one tells the second operator the send needs no
 * decision, which is the same class of silence this whole change exists to
 * remove.
 */
export type ApprovalLookupState = 'loading' | 'error' | 'ready';

/** One page of the approvals queue, as the list route returns it. */
export interface ApprovalPage {
  readonly data: readonly PendingActionItem[];
  readonly meta: { readonly next_cursor: string | null };
}

export const APPROVAL_LOOKUP_PAGE_SIZE = 100;
/** 1,000 open approvals is an incident in its own right, not a page to walk. */
export const APPROVAL_LOOKUP_MAX_PAGES = 10;

/**
 * The broadcast's open approval request, found by walking the approvals queue.
 *
 * A single page is not enough: the queue is cursor-paginated and holds every
 * operation, so a busy console can push a still-valid broadcast request off
 * page one -- and reading that as "no request" would hide the decision
 * controls from the operator who has to make the decision. Running out of
 * pages throws rather than returning null, because at that point the console
 * does not know, and saying "none" would be a guess.
 */
export async function findPendingApproval(
  broadcastId: string,
  fetchPage: (cursor: string | undefined) => Promise<ApprovalPage>
): Promise<PendingActionItem | null> {
  return walkPendingApprovals(
    actions => pendingApprovalFor(actions, broadcastId),
    fetchPage,
    'Could not find this broadcast in the approvals queue. Decide it on the Approvals page.'
  );
}

/**
 * Walks the cursor-paginated approvals queue until `match` finds its request.
 * Shared by every page that has to know whether ITS operation is already
 * parked: null means "looked at everything, there is none"; running out of
 * pages throws `exhaustedMessage`, because past that point "none" is a guess.
 */
export async function walkPendingApprovals(
  match: (actions: readonly PendingActionItem[]) => PendingActionItem | null,
  fetchPage: (cursor: string | undefined) => Promise<ApprovalPage>,
  exhaustedMessage: string
): Promise<PendingActionItem | null> {
  let cursor: string | undefined;

  for (let page = 0; page < APPROVAL_LOOKUP_MAX_PAGES; page += 1) {
    const result = await fetchPage(cursor);
    const found = match(result.data);
    if (found !== null) return found;

    const next = result.meta.next_cursor;
    if (next === null) return null;
    cursor = next;
  }

  throw new Error(exhaustedMessage);
}

/**
 * What this viewer may do with a broadcast's approval request.
 *
 * DM-6208 follow-up: the second operator had no reason to know the decision
 * lives on a different page, so they opened the broadcast, found nothing to
 * click, and stopped. The decision belongs where the message is.
 */
export function approvalGate(
  action: PendingActionItem | null,
  viewerSub: string | undefined,
  viewerHasDecideRole: boolean,
  lookup: ApprovalLookupState = 'ready'
): ApprovalGate {
  if (lookup === 'loading') {
    return { canDecide: false, note: 'Looking for the open approval request…' };
  }
  if (lookup === 'error') {
    return {
      canDecide: false,
      note: 'The approval request could not be loaded, so it is not safe to say whether one is open. Retry, or decide it on the Approvals page.',
    };
  }
  if (action === null) {
    return {
      canDecide: false,
      note: 'No open approval request. Whoever drafted this has to request approval first.',
    };
  }
  if (viewerSub !== undefined && action.requested_by === viewerSub) {
    return {
      canDecide: false,
      note: 'You requested this send, so you cannot approve it. A different operator has to.',
    };
  }
  if (!viewerHasDecideRole) {
    return {
      canDecide: false,
      note: 'Deciding an approval requires the admin or admin-operator role.',
    };
  }
  return {
    canDecide: true,
    note: 'Approving does not send immediately: the broadcast is scheduled and the dispatcher picks it up on its next run.',
  };
}

/**
 * Why a test send cannot be submitted yet, or null.
 *
 * The operator names an app account because admin sign-in and app accounts are
 * separate Cognito pools -- an operator's own admin identity is not a user the
 * notification stack can address.
 */
export function testTargetBlocker(email: string): string | null {
  const trimmed = email.trim();
  if (trimmed.length === 0) return 'Enter the app account to test against';
  if (!trimmed.includes('@') || trimmed.length > 254) return 'Enter a valid email address';
  return null;
}
